import React from 'react';
import type { TextFieldProps } from '@mui/material';

import TextField from './TextField';
import DateField from './DateField';
import TimeField from './TimeField';
import NumberField from './NumberField';
import TextArea from './TextArea';
import TextAutocomplete from './TextAutocomplete';
import SelectField from './SelectField';
import CheckboxField from './CheckboxField';
import CheckboxSelectField from './CheckboxSelectField';

import type { Field } from '../types/form';
import LinkField from './LinkField';
import RichTextEditor from './richTextEditor/RichTextEditor';

type Props<FT> = TextFieldProps & {
  field: Field<FT>;
  value: unknown;
  handleChange: (value: unknown) => void;
  handleBlur?: (value: unknown) => void;
};

function FormComponent<FT>(props: Props<FT>) {
  const { field, value, helperText, handleChange, handleBlur, ...rest } = props;

  switch (field.fieldTypes) {
    case 'text':
      return (
        <TextField
          label={field.label}
          value={value}
          helperText={helperText}
          validateIsNotEmpty={field.validateIsNotEmpty}
          handleChange={handleChange}
          handleBlur={handleBlur}
          {...rest}
        />
      );
    case 'inputLink':
      return (
        <LinkField
          label={field.label}
          value={value}
          helperText={helperText}
          validateIsNotEmpty={field.validateIsNotEmpty}
          handleChange={handleChange}
          handleBlur={handleBlur}
          {...rest}
        />
      );
    case 'date':
      return (
        <DateField
          label={field.label}
          value={value}
          helperText={helperText}
          validateIsNotEmpty={field.validateIsNotEmpty}
          handleChange={handleChange}
          handleBlur={handleBlur}
          {...rest}
        />
      );
    case 'time':
      return (
        <TimeField
          label={field.label}
          value={value}
          helperText={helperText}
          validateIsNotEmpty={field.validateIsNotEmpty}
          handleChange={handleChange}
          handleBlur={handleBlur}
          {...rest}
        />
      );
    case 'number':
      return (
        <NumberField
          label={field.label}
          value={value}
          steps={field.steps}
          helperText={helperText}
          validateIsNotEmpty={field.validateIsNotEmpty}
          handleChange={handleChange}
          handleBlur={handleBlur}
          {...rest}
        />
      );
    case 'textArea':
      return (
        <TextArea
          label={field.label}
          rows={field.rows}
          value={value}
          helperText={helperText}
          validateIsNotEmpty={field.validateIsNotEmpty}
          handleChange={handleChange}
          handleBlur={handleBlur}
          {...rest}
        />
      );
    case 'richTextArea':
      return (
        <RichTextEditor
          label={field.label}
          rows={field.rows}
          value={value}
          handleChange={handleChange}
          handleBlur={handleBlur}
        />
      );
    case 'autocomplete':
      return (
        <TextAutocomplete
          label={field.label}
          options={field.options}
          value={value}
          validateIsNotEmpty={field.validateIsNotEmpty}
          handleChange={handleChange}
          handleBlur={handleBlur}
        />
      );
    case 'select':
      return (
        <SelectField
          label={field.label}
          options={field.options}
          value={value}
          helperText={helperText}
          validateIsNotEmpty={field.validateIsNotEmpty}
          handleChange={handleChange}
          handleBlur={handleBlur}
          {...rest}
        />
      );
    case 'checkbox':
      return (
        <CheckboxField
          label={field.label}
          value={value}
          handleChange={handleChange}
          handleBlur={handleBlur}
          {...rest}
        />
      );
    case 'checkboxSelect':
      return (
        <CheckboxSelectField
          label={field.label}
          options={field.options}
          value={value}
          handleChange={handleChange}
          handleBlur={handleBlur}
        />
      );

    default:
      return null;
  }
}

export default FormComponent;
